import React, { Fragment, forwardRef } from 'react';
import PropTypes from 'prop-types';

import Link from 'Components/Link';
import Icon from 'Components/Icon';
import styles from './styles.scss';
import classNames from 'classnames/bind';
import { gaPush } from 'Utils/ga_push';
import { isExternalLink, validButtonVariants, isValidTarget, purifyHtmlToReact } from 'Utils';
import { isSpecialTracked } from 'Utils/trackingAttributes';

const cx = classNames.bind(styles);

class Button extends React.Component {
  static propTypes = {
    additional: PropTypes.string,
    additionalProps: PropTypes.object,
    ariaControls: PropTypes.string,
    ariaExpanded: PropTypes.bool,
    ariaLabel: PropTypes.string,
    ariaLabelledby: PropTypes.string,
    big: PropTypes.bool,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    e2e: PropTypes.string,
    forwardedRef: PropTypes.object,
    header: PropTypes.bool,
    href: PropTypes.string,
    icon: PropTypes.string,
    id: PropTypes.string,
    label: PropTypes.string,
    light: PropTypes.bool,
    name: PropTypes.string,
    onClick: PropTypes.func,
    onMouseOut: PropTypes.func,
    onMouseOver: PropTypes.func,
    screenReaderLabel: PropTypes.string,
    small: PropTypes.bool,
    target: PropTypes.string,
    textEnd: PropTypes.node,
    track: PropTypes.bool,
    type: PropTypes.oneOf([...validButtonVariants, null]),
    variant: PropTypes.oneOf(validButtonVariants),
    upperCase: PropTypes.bool,
  };

  static defaultProps = {
    disabled: false,
    label: '',
    target: null,
    type: 'primary',
    variant: 'primary',
  };

  onClick = (event) => {
    this.track(event);
    const { onClick, variant, disabled } = this.props;

    if (!disabled && onClick) {
      // events only are necessary for secondaries since we need to prevent the link-follow
      // if we don't drop the event for primaries f.e. proxies are passed and this causes
      // crashes in random spots of the project which rely somehow on event = undefined :|
      onClick(variant === 'secondary' ? event : undefined);
    }
  };

  onMouseOver = () => {
    if (!this.props.disabled && this.props.onMouseOver) {
      this.props.onMouseOver();
    }
  };

  onMouseOut = () => {
    if (!this.props.disabled && this.props.onMouseOut) {
      this.props.onMouseOut();
    }
  };

  track = (event) => {
    if (isSpecialTracked(event.target)) {
      return;
    }

    // hotfix track only if Versicherung button clicked
    if (this.props.track) {
      gaPush({
        event: 'gaEvent',
        category: 'CTA',
        action: 'button click',
        label: 'Versicherung scroll to Form',
      });
    }
  };

  renderLabel = (variant, rawLabel, upperCase) => {
    const label = purifyHtmlToReact(rawLabel);

    return variant === 'primary' ? (
      <span className={styles.button_rolloverwrapper}>
        <span className={styles.button_rollovertext} aria-hidden={upperCase ? 'true' : undefined}>
          {label}
          <span aria-hidden="true" className={styles.button_rollovertext_1}>
            {label}
          </span>
          <span aria-hidden="true" className={styles.button_rollovertext_2}>
            {label}
          </span>
        </span>
      </span>
    ) : (
      <span>{label}</span>
    );
  };

  render() {
    const {
      additional,
      additionalProps,
      ariaControls,
      ariaExpanded,
      ariaLabel,
      ariaLabelledby,
      big,
      className,
      disabled,
      e2e,
      forwardedRef,
      header,
      icon,
      id,
      label,
      light,
      name,
      resetLineHeight,
      screenReaderLabel,
      small,
      target,
      textEnd,
      trackingAction,
      trackingLabel,
      variant,
      upperCase = true,
    } = this.props;

    let href = this.props.href;

    if (
      window.dealer.settings.customTopCars.isActive &&
      href &&
      href.includes('beratung-und-kauf/fahrzeugboerse')
    ) {
      href = '/?modaltype=TOPCARS&showmodal=true';
    }

    const btnClass = cx(
      'btn',
      `btn--variant-${variant}`,
      {
        'btn--state-disabled': disabled,
        // special style availbale for primary variant only
        'btn--style-header': header,
        'btn--style-icon': icon,
        'btn--style-light': light,
        'btn--style-not-uppercase': !upperCase,
        // special variant used in Card component
        'btn--style-reset-line-height': resetLineHeight,
        'btn--style-small': small || (icon && !big),
      },
      className
    );
    const inlineIcon = variant === 'tertiary';

    if (href) {
      const isExternal = isExternalLink(href);
      let propTarget = isValidTarget(target) ? target : undefined;
      if (isExternal && !propTarget) {
        propTarget = '_blank';
      }

      return (
        <Link
          additionalProps={additionalProps}
          ariaLabel={ariaLabel}
          label={label}
          ariaLabelledby={ariaLabelledby}
          className={cx('cta', btnClass)}
          e2e={e2e}
          isExternal={isExternal}
          onClick={this.onClick}
          onMouseOut={this.onMouseOut}
          onMouseOver={this.onMouseOver}
          screenReaderLabel={screenReaderLabel !== '' ? screenReaderLabel : undefined}
          target={propTarget}
          to={href + (additional || '')}
          trackingAction={trackingAction}
          trackingLabel={trackingLabel || label.toLowerCase()}
          ref={forwardedRef}
        >
          <Fragment>
            {icon && <Icon name={icon} inline={inlineIcon} />}
            {this.renderLabel(variant, label, upperCase)}
            {textEnd}
          </Fragment>
        </Link>
      );
    }

    return (
      <button
        aria-controls={ariaControls}
        aria-expanded={[true, false].includes(ariaExpanded) ? String(ariaExpanded) : undefined}
        aria-label={ariaLabel ?? `${label ?? ''} ${screenReaderLabel ?? ''}`.trim()}
        aria-labelledby={ariaLabelledby}
        className={cx('cta', btnClass)}
        data-e2e={e2e}
        data-track-action={trackingAction}
        data-tracking-label={trackingLabel || label.toLowerCase()}
        disabled={disabled}
        id={id}
        name={name || label}
        onClick={this.onClick}
        onMouseOut={this.onMouseOut}
        onMouseOver={this.onMouseOver}
        ref={forwardedRef}
        {...additionalProps}
      >
        {icon && <Icon name={icon} inline={inlineIcon} />}
        {this.renderLabel(variant, label, upperCase)}
        {textEnd}
      </button>
    );
  }
}

export default forwardRef((props, ref) => <Button {...props} forwardedRef={ref} />);
